import { CommonModule, isPlatformBrowser } from '@angular/common';
import type {
  AfterViewInit,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  Optional,
  Output,
  PLATFORM_ID,
  SecurityContext,
  ViewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Pwa } from '@freelancer/pwa';
import type { GalleryItem } from '@freelancer/ui/gallery-types';
import { GalleryItemType } from '@freelancer/ui/gallery-types';
import { DEFAULT_DPR1_SCALING_FACTOR } from '@freelancer/ui/picture';
import { UserAgent } from '@freelancer/user-agent';
import { isDefined } from '@freelancer/utils';
import type { GalleryItem as LightGalleryItem } from 'lightgallery/lg-utils';
import { Assets } from '../assets';
import type { ImageFileType, VideoFileType } from '../helpers/helpers';
import {
  generateUniqueID,
  getFileExtension,
  resolveAssetUrl,
} from '../helpers/helpers';
import { MediaViewerLightGalleryComponent } from './media-viewer-light-gallery/media-viewer-light-gallery.component';
import { createVideoSource } from './media-viewer-light-gallery/media-viewer-light-gallery.helpers';
import {
  lightGalleryItemSelector,
  lightGalleryThumbnailProperty,
} from './media-viewer-light-gallery/media-viewer-light-gallery.types';
import { TESTING_MEDIA_VIEWER_DISABLED } from './media-viewer.config';
import type { MediaItem } from './media-viewer.types';
import {
  DEFAULT_FILE_THUMBNAIL,
  DEFAULT_FILE_THUMBNAIL_FILE_TYPE,
  DEFAULT_FILE_THUMBNAIL_HEIGHT,
  DEFAULT_FILE_THUMBNAIL_WIDTH,
  DEFAULT_VIDEO_FILE_THUMBNAIL,
  MediaViewerMode,
} from './media-viewer.types';

@Component({
  standalone: true,
  selector: 'fl-media-viewer-item-caption',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./media-viewer-item-caption.component.scss'],
})
export class MediaViewerItemCaptionComponent {}

@Component({
  standalone: true,
  selector: 'fl-media-viewer-item-content',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaViewerItemContentComponent {}

@Component({
  standalone: true,
  selector: lightGalleryItemSelector,
  template: `
    <ng-content select="fl-media-viewer-item-content"></ng-content>
    <div
      [attr.id]="captionDomId"
      class="CaptionContainer"
    >
      <ng-content select="fl-media-viewer-item-caption"></ng-content>
    </div>
  `,
  styleUrls: ['./media-viewer-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaViewerItemComponent implements OnChanges, OnInit {
  @Input()
  @HostBinding('attr.data-alt')
  alt: string;

  @Input() source: string;

  @Input() thumbnailSource?: string;

  @Input()
  @HostBinding('attr.data-width')
  width?: number;

  @Input() height?: number;

  @Input()
  type: GalleryItemType = GalleryItemType.PICTURE;

  @Input()
  @HostBinding('attr.data-download')
  fileName?: string;

  @Input()
  fileType?: ImageFileType | VideoFileType;

  @Input()
  @HostBinding('attr.data-download-url')
  downloadUrl?: string;

  @Input()
  @HostBinding('attr.data-srcset')
  srcset?: string;

  @Input()
  @HostBinding('attr.data-sizes')
  sizes?: string;

  @HostBinding('attr.data-src')
  lightGallerySrc?: string;

  @HostBinding('attr.data-video')
  lightGalleryVideoSrc?: string;

  @HostBinding(`attr.${lightGalleryThumbnailProperty}`)
  lightGalleryThumbnail?: string;

  @HostBinding('attr.data-lg-size')
  lightGallerySize?: `${number}-${number}`;

  @HostBinding('attr.data-sub-html')
  lightGalleryCaptionSelector!: string;

  captionDomId!: string;

  readonly defaultFileThumbnail = resolveAssetUrl(
    this.assets,
    DEFAULT_FILE_THUMBNAIL,
  );
  constructor(private assets: Assets) {}

  ngOnInit(): void {
    this.captionDomId = `media_item_caption_${generateUniqueID()}`;
    this.lightGalleryCaptionSelector = `#${this.captionDomId}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      ('width' in changes || 'height' in changes) &&
      isDefined(this.width) &&
      isDefined(this.height)
    ) {
      this.lightGallerySize = `${this.width}-${this.height}`;
    }

    if (
      'thumbnailSource' in changes ||
      'source' in changes ||
      'type' in changes ||
      'fileType' in changes
    ) {
      this.lightGallerySrc = undefined;
      this.lightGalleryVideoSrc = undefined;
      this.lightGalleryThumbnail = undefined;

      switch (this.type) {
        case GalleryItemType.PICTURE: {
          this.lightGallerySrc = resolveAssetUrl(this.assets, this.source);
          this.lightGalleryThumbnail = this.thumbnailSource
            ? resolveAssetUrl(this.assets, this.thumbnailSource)
            : this.lightGallerySrc;

          break;
        }
        case GalleryItemType.VIDEO: {
          this.lightGalleryVideoSrc = JSON.stringify(
            createVideoSource(
              resolveAssetUrl(this.assets, this.source),
              this.fileType,
            ),
          );
          this.lightGalleryThumbnail = this.thumbnailSource
            ? resolveAssetUrl(this.assets, this.thumbnailSource)
            : undefined;

          break;
        }
        case GalleryItemType.PDF: {
          break;
        }
        default: {
          this.lightGallerySrc = this.defaultFileThumbnail;
          this.lightGalleryThumbnail = this.thumbnailSource
            ? resolveAssetUrl(this.assets, this.thumbnailSource)
            : this.defaultFileThumbnail;
        }
      }
    }
  }
}

@Component({
  standalone: true,
  selector: 'fl-media-viewer',
  imports: [CommonModule, MediaViewerLightGalleryComponent],
  template: `
    <fl-media-viewer-light-gallery
      #lightGalleryComponent
      *ngIf="!testingMediaViewerDisabled"
      [mode]="mode"
      [ariaDescribedBy]="ariaDescribedBy"
      [ariaLabelledBy]="ariaLabelledBy"
      [enableFullscreen]="enableFullscreen"
      [enablePhotoRotations]="photoRotations"
      [lightGalleryItems]="lightGalleryItems"
      [shouldLoopBack]="shouldLoopBack"
      [showCloseButton]="closeable"
      [showCounter]="showCounter"
      [showDownloadButton]="downloadable"
      [slideTransition]="'slide'"
    >
      <ng-content></ng-content>
    </fl-media-viewer-light-gallery>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaViewerComponent implements OnChanges, AfterViewInit {
  @Input()
  @HostBinding('attr.data-mode')
  mode: MediaViewerMode = 'inline';

  /**
   * Items to be displayed when the gallery is in modal mode.
   */
  @Input() galleryItems?: readonly MediaItem[] | readonly GalleryItem[];
  /**
   * Whether the gallery should loop back to the first item after the last item.
   */
  @Input() shouldLoopBack = true;
  @Input() closeable = true;
  @Input() showCounter = true;
  @Input() downloadable = true;
  /**
   * Whether to allow the user to rotate the photo in the media.
   */
  @Input() photoRotations = false;
  @Input() enableFullscreen = false;
  @Input() ariaDescribedBy?: string;
  @Input() ariaLabelledBy?: string;

  @Output() loaded = new EventEmitter<void>();

  @ViewChild('lightGalleryComponent')
  lightGalleryComponent: MediaViewerLightGalleryComponent;
  lightGalleryItems?: readonly LightGalleryItem[];

  readonly defaultFileThumbnail = resolveAssetUrl(
    this.assets,
    DEFAULT_FILE_THUMBNAIL,
  );
  readonly defaultVideoThumbnail = resolveAssetUrl(
    this.assets,
    DEFAULT_VIDEO_FILE_THUMBNAIL,
  );

  readonly TEXT_NOT_SUPPORTED_FILE = $localize`This file cannot be previewed.`;
  readonly TEXT_NOT_SUPPORTED_DOWNLOAD = $localize`Download`;
  readonly TEXT_NOT_SUPPORT_DOWNLOAD_ARIA = $localize`Download button`;

  constructor(
    private assets: Assets,
    private sanitizer: DomSanitizer,
    private pwa: Pwa,
    private userAgent: UserAgent,
    @Optional()
    @Inject(TESTING_MEDIA_VIEWER_DISABLED)
    public readonly testingMediaViewerDisabled = false,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'galleryItems' in changes &&
      changes.galleryItems.currentValue &&
      this.mode === 'modal'
    ) {
      this.lightGalleryItems = changes.galleryItems.currentValue.map(
        (item: MediaItem): LightGalleryItem => {
          const {
            alt,
            downloadUrl,
            fileName,
            height,
            source,
            thumbnailSource,
            type,
            width,
          } = item;

          const src = resolveAssetUrl(this.assets, source);

          const mediaItem: Partial<LightGalleryItem> = {
            downloadUrl: downloadUrl ?? src,
            alt,
            fileName,
            height,
            width: isDefined(width) ? width.toString() : undefined,
            subHtml: this.createCaption(item),
          };

          if (type === GalleryItemType.PICTURE) {
            const { srcset, sizes, fileType, thumbnailOptimizationParameters } =
              item;
            let thumb = thumbnailSource
              ? resolveAssetUrl(this.assets, thumbnailSource)
              : src;

            if (thumbnailOptimizationParameters) {
              const scalingFactor = this.getScalingFactor();
              const {
                width: thumbWidth,
                height: thumbHeight,
                cropToFit,
              } = thumbnailOptimizationParameters;
              const thumbUrl = new URL(thumb);
              if (thumbWidth) {
                thumbUrl.searchParams.set(
                  'width',
                  Math.floor(thumbWidth * scalingFactor).toString(),
                );
              }
              if (thumbHeight) {
                thumbUrl.searchParams.set(
                  'height',
                  Math.floor(thumbHeight * scalingFactor).toString(),
                );
              }
              // The `fit` parameter is only valid if both width and height are specified.
              // When either height or width is not provided but fit is specified,
              // image optimization will be skipped by Fastly.
              if (cropToFit && thumbWidth && thumbHeight) {
                thumbUrl.searchParams.set('fit', 'crop');
              }

              thumbUrl.searchParams.set('image-optimizer', 'force');

              thumb = thumbUrl.toString();
            }

            return {
              ...mediaItem,
              src,
              thumb,
              srcset,
              sizes,
              type: fileType
                ? `image/${fileType}`
                : `image/${getFileExtension(fileName)}`,
            };
          }

          if (type === GalleryItemType.VIDEO) {
            const { fileType, posterSrc } = item;
            const poster = posterSrc
              ? resolveAssetUrl(this.assets, posterSrc)
              : undefined;
            const thumb = thumbnailSource
              ? resolveAssetUrl(this.assets, thumbnailSource)
              : this.defaultVideoThumbnail;
            const videoWidth = thumbnailSource
              ? mediaItem.width
              : DEFAULT_FILE_THUMBNAIL_WIDTH;
            const videoHeight = thumbnailSource
              ? mediaItem.height
              : DEFAULT_FILE_THUMBNAIL_HEIGHT;

            return {
              ...mediaItem,
              thumb,
              poster,
              width: videoWidth,
              height: videoHeight,
              video: createVideoSource(src, fileType),
            };
          }

          // Only show PDF in modal mode if the PWA is not Safari on web.
          if (
            type === GalleryItemType.PDF &&
            this.pwa.getPlatform() === 'web' &&
            this.userAgent.getUserAgent().getBrowser().name !== 'Safari' &&
            !this.userAgent.isMobileDevice()
          ) {
            const pdfUrl = new URL(src);
            pdfUrl.searchParams.set('disposition', 'inline');

            return {
              ...mediaItem,
              thumb: this.defaultFileThumbnail,
              src: pdfUrl.toString(),
              type: 'application/pdf',
              width: DEFAULT_FILE_THUMBNAIL_WIDTH,
              height: DEFAULT_FILE_THUMBNAIL_HEIGHT,
              iframe: true,
            };
          }

          // Type is GalleryItemType.FILE
          return {
            ...mediaItem,
            src: this.defaultFileThumbnail,
            thumb: thumbnailSource
              ? resolveAssetUrl(this.assets, thumbnailSource)
              : this.defaultFileThumbnail,
            type: DEFAULT_FILE_THUMBNAIL_FILE_TYPE,
            width: DEFAULT_FILE_THUMBNAIL_WIDTH,
            height: DEFAULT_FILE_THUMBNAIL_HEIGHT,
          };
        },
      );
    }
  }

  ngAfterViewInit(): void {
    this.loaded.emit();
  }

  goToItem(index: number): void {
    this.lightGalleryComponent.goToItem(index);
  }

  openModal(index?: number): void {
    this.lightGalleryComponent.openModal(index);
  }

  isModalOpen(): boolean {
    return this.lightGalleryComponent.isModalOpen();
  }

  private createCaption(item: MediaItem): string {
    if (
      [
        GalleryItemType.PICTURE,
        GalleryItemType.VIDEO,
        GalleryItemType.PDF,
      ].includes(item.type)
    ) {
      return `
        <p><b>${this.sanitizer.sanitize(
          SecurityContext.HTML,
          item.fileName,
        )}</b></p>
      `;
    }

    return `
      <p><b>${this.sanitizer.sanitize(
        SecurityContext.HTML,
        item.fileName,
      )}</b></p>
      <p>${this.TEXT_NOT_SUPPORTED_FILE}</p>
      <p>
        <a
          aria-label="${this.TEXT_NOT_SUPPORT_DOWNLOAD_ARIA}"
          class="LG-not-supported-download-button"
          download
          href="${item.downloadUrl}"
          rel="noopener"
          target="_blank"
        >
          ${this.TEXT_NOT_SUPPORTED_DOWNLOAD}
        </a>
      </p>
    `;
  }

  private getScalingFactor(): number {
    if (!isPlatformBrowser(this.platformId)) {
      return DEFAULT_DPR1_SCALING_FACTOR;
    }

    // eslint-disable-next-line no-restricted-globals, no-restricted-properties
    const { devicePixelRatio } = window;
    return devicePixelRatio > 1
      ? devicePixelRatio
      : DEFAULT_DPR1_SCALING_FACTOR;
  }
}

export const MEDIA_VIEWER_DIRECTIVES = [
  MediaViewerItemCaptionComponent,
  MediaViewerItemContentComponent,
  MediaViewerItemComponent,
  MediaViewerComponent,
];
